import * as React from "react";
import Chip from "@mui/material/Chip";
import type { ChipProps } from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import { Buildings as InternalIcon } from "@phosphor-icons/react/Buildings";
import { Eye as PrivateIcon } from "@phosphor-icons/react/Eye";
import { Globe as PublicIcon } from "@phosphor-icons/react/Globe";
import { Link as LinkIcon } from "@phosphor-icons/react/Link";
import { Lock as PasswordProtectedIcon } from "@phosphor-icons/react/Lock";

import type { AccessMode } from "../access-mode-button";

export interface AccessModeIndicatorProps {
	accessMode: AccessMode;
	size?: "small" | "medium";
	context?: "map" | "dataset";
}

interface AccessModeConfig {
	label: string;
	description: string;
	datasetDescription?: string;
	color: ChipProps["color"];
	icon: React.ComponentType<{ size?: string | number }>;
}

const ACCESS_MODE_CONFIG: Record<AccessMode, AccessModeConfig> = {
	public: {
		label: "Public",
		description: "Accessible to anyone with the URL.",
		datasetDescription: "Visible in the data portal to everyone.",
		color: "success",
		icon: PublicIcon,
	},
	standalone: {
		label: "Public",
		description: "Accessible to anyone with the URL.",
		color: "success",
		icon: LinkIcon,
	},
	hidden: {
		label: "Link",
		description:
			"Accessible only through the private share link (hidden URL), not the standard public map URL.",
		color: "info",
		icon: LinkIcon,
	},
	password_protected: {
		label: "Password",
		description: "Requires a password in addition to the URL.",
		color: "warning",
		icon: PasswordProtectedIcon,
	},
	internal: {
		label: "Internal",
		description: "Restricted to authorised account users and groups.",
		color: "default",
		icon: InternalIcon,
	},
	private: {
		label: "Private",
		description: "Requires authenticated access.",
		color: "default",
		icon: PrivateIcon,
	},
};

export function AccessModeIndicator({
	accessMode,
	size = "small",
	context = "map",
}: AccessModeIndicatorProps): React.JSX.Element {
	const config = ACCESS_MODE_CONFIG[accessMode];
	const iconSize = size === "small" ? 14 : 16;
	const IconComponent = config.icon;
	const itemType = context === "dataset" ? "Dataset" : "Map";
	const description =
		context === "dataset" && config.datasetDescription
			? config.datasetDescription
			: config.description;
	const ariaDescription = description;

	return (
		<Tooltip
			arrow
			enterDelay={200}
			title={description}>
			<Chip
				size={size}
				variant="outlined"
				color={config.color}
				icon={<IconComponent size={iconSize} />}
				label={config.label}
				aria-label={`${itemType} access: ${config.label}. ${ariaDescription}`}
				sx={{
					fontWeight: 500,
					"& .MuiChip-label": {
						px: size === "small" ? 0.75 : 1,
					},
					"& .MuiChip-icon": {
						ml: size === "small" ? 0.5 : 0.75,
					},
				}}
			/>
		</Tooltip>
	);
}
